import React, { useCallback, useRef } from 'react';
import Link from 'components/link';
import styles from './style.module.scss';
import { buildClassName } from 'utils/build-class-name';
import { listingPageModalIds } from 'constants/test-constants';
import { useIsomorphicLayoutEffect } from 'types/react';
import { useModalContext } from 'contexts/modal';
import { ComplianceRequirement } from 'types/compliance';
import { useTranslation } from 'next-i18next';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';
import { isObfuscatedAddressPath } from 'data/addresses/go-search/addressPathParser';
import { getSeoPropertyTypeLabelKey } from 'types/reso';

import type { PropsWithTestId } from 'types';

type AddressHeadingProps = PropsWithTestId<{
  complianceRequirement: ComplianceRequirement;
  addressPath: string;
  address: string;
  propertyType: string;
  onAddressClick?: () => void;
  listingSource?: string;
}>;

function StreetAddress({ complianceRequirement, address, propertyType, testId, onAddressClick }: Pick<AddressHeadingProps, 'complianceRequirement' | 'address' | 'propertyType' | 'testId' | 'onAddressClick' >) {
  const { t } = useTranslation(Namespaces.COMMON);
  const shouldBlur = complianceRequirement !== ComplianceRequirement.NONE;
  return (
    <div className={buildClassName((shouldBlur && 'blur'), styles.street)} onClick={onAddressClick} data-testid={testId}>
      <h1 itemProp={'streetAddress'} data-testid={listingPageModalIds.listingAddress}>
        {address ?
          address :
          t(LOCALE_COMMON_PATHS.ADDRESS_HEADING_ADDRESS_UNKNOWN,
            {
              defaultValue: 'Address not available',
            }
          )}
      </h1>
      <h2 className={shouldBlur ? 'blur' : ''}>
        {t(getSeoPropertyTypeLabelKey(propertyType), {
          defaultValue: propertyType || 'Real Estate',
        })}
      </h2>
    </div>
  );
}

export default function AddressHeading({ complianceRequirement, addressPath, address, propertyType, testId, onAddressClick, listingSource }: AddressHeadingProps) {
  const { closeAllModals } = useModalContext();

  const closeAllModalsRef = useRef(closeAllModals);
  const onAddressClickRef = useRef(onAddressClick);

  useIsomorphicLayoutEffect(() => {
    closeAllModalsRef.current = closeAllModals;
    onAddressClickRef.current = onAddressClick;
  }, [closeAllModals, onAddressClick]);

  const handleAddressLinkClick = useCallback(() => {
    onAddressClickRef.current?.();
    closeAllModalsRef.current?.();
  }, []);

  if (!addressPath) {
    return <StreetAddress testId={testId}
      complianceRequirement={complianceRequirement}
      address={address}
      propertyType={propertyType}
      onAddressClick={handleAddressLinkClick}
    />;
  } else {
    const rel = isObfuscatedAddressPath(addressPath) ? 'nofollow' : undefined;
    const href = listingSource === 'client-app-fallback'
      ? `${addressPath}?listingSource=client-app-fallback`
      : addressPath;
    return <Link testId={testId} href={href} onClick={handleAddressLinkClick} rel={rel} className={styles.addressLink} >
      <StreetAddress testId={testId}
        complianceRequirement={complianceRequirement}
        address={address}
        propertyType={propertyType}
        onAddressClick={handleAddressLinkClick}
      />
    </Link>;
  }
}

